import { Action, TerminateReasonEnum, ICallQualityOptions } from "../models";
import { Observable, Subject } from "rxjs";
import { AppConfigService as Config, Log } from "../services";

export enum windowActionType {
  READY = "READY",
  LEAVE_CALL = "LEAVE_CALL",
  CALL_ENDED = "CALL_ENDED",
  CALL_STARTED = "CALL_STARTED",
  CALL_ON_HOLD = "CALL_ON_HOLD",
  CALL_QUALITY_OPTIONS = "CALL_QUALITY_OPTIONS",
  FEEDBACK_REQUESTED = "FEEDBACK_REQUESTED",
  AGENT_MUTED = "AGENT_MUTED",
  AGENT_VOLUME_CHANGE = "AGENT_VOLUME_CHANGE",
  DEVICE_CAPTURE = "DEVICE_CAPTURE",
  DEVICE_CAPTURE_ERROR = "DEVICE_CAPTURE_ERROR",
  DEVICE_CAPTURE_SUCCESS = "DEVICE_CAPTURE_SUCCESS",
  DEVICE_CAPTURE_STOP = "DEVICE_CAPTURE_STOP",
  DEVICE_CAPTURE_STOP_SUCCESS = "DEVICE_CAPTURE_STOP_SUCCESS",
  MUTE_CHANGE_REQUESTED = "MUTE_CHANGE_REQUESTED",
  MUTE_CHANGED = "MUTE_CHANGED",
  SCREEN_SHARE_AVAILABILITY_CHANGED = "SCREEN_SHARE_AVAILABILITY_CHANGED",
  COBROWSE_VIEW_REQUEST = "COBROWSE_VIEW_REQUEST",
  COBROWSE_TERMINATE_REQUEST = "COBROWSE_TERMINATE_REQUEST",
  COBROWSE_STARTED = "COBROWSE_STARTED",
  COBROWSE_TERMINATED = "COBROWSE_TERMINATED",
  CONSENT_REQUEST = "CONSENT_REQUEST",
  CONSENT_RESPONSE = "CONSENT_RESPONSE",
  THEME_READY = "THEME_READY",
  TRANSFER = "TRANSFER",
  CUSTOMER_METADATA_READY = "CUSTOMER_METADATA_READY",
  RECORDER_STARTED = "RECORDER_STARTED",
  RECORDER_ENDED = "RECORDER_ENDED",
  TEST_DEVICE_PERMISSIONS_SUCCESS = "TEST_DEVICE_PERMISSIONS_SUCCESS",
  TEST_DEVICE_PERMISSIONS_ERROR = "TEST_DEVICE_PERMISSIONS_ERROR",
  CONVERSATION_PANEL_TOGGLE = "CONVERSATION_PANEL_TOGGLE",
}

export class LeaveCallAction implements Action {
  type = windowActionType.LEAVE_CALL;
  constructor(public payload: TerminateReasonEnum) {}
}

export class DeviceCaptureAction implements Action {
  type = windowActionType.DEVICE_CAPTURE;
}

export class DeviceCaptureStopAction implements Action {
  type = windowActionType.DEVICE_CAPTURE_STOP;
}

export class MuteChangeRequestedAction implements Action {
  type = windowActionType.MUTE_CHANGE_REQUESTED;
  constructor(public payload: "audio" | "video") {}
}

export class CoBrowseTerminatedAction implements Action {
  type = windowActionType.COBROWSE_TERMINATED;
}

export class ScreenShareAvailabilityChangedAction implements Action {
  type = windowActionType.SCREEN_SHARE_AVAILABILITY_CHANGED;
  constructor(public payload: boolean) {}
}

export class ThemeReadyAction implements Action {
  type = windowActionType.THEME_READY;
  constructor(public payload: any) {}
}

export class CustomerMetadataReadyAction implements Action {
  type = windowActionType.CUSTOMER_METADATA_READY;
  constructor(public payload: { [key: string]: any }) {}
}
export class ToggleConversationPanelAction implements Action {
  type = windowActionType.CONVERSATION_PANEL_TOGGLE;
  constructor(public payload: boolean) {}
}

export class SetCallQualityOptionsAction implements Action {
  type = windowActionType.CALL_QUALITY_OPTIONS;
  constructor(public payload: ICallQualityOptions) {}
}

export class CobrowseStartedAction implements Action {
  type = windowActionType.COBROWSE_STARTED;
  constructor() {}
}

export class CobrowseTerminateRequestAction implements Action {
  type = windowActionType.COBROWSE_TERMINATE_REQUEST;
  constructor() {}
}

export class WindowEventServiceCtr {
  private windowMessageSubject: Subject<Action>;
  private target: HTMLIFrameElement;
  private targetPopup: Window;

  /**
   * Subscribes to window 'message' topic and returns the events that only apply to this app.
   * The same channel is used by pureCloud so we need to discard those messages.
   */
  public reset(ticketUrl: string): Observable<Action> {
    // extract the url origin from the auvious ticket
    // in order to send messages to iFrame
    Config.iFrameOrigin = ticketUrl;
    this.windowMessageSubject = new Subject<Action>();
    window.addEventListener("message", this.onMessage);

    return this.windowMessageSubject;
  }

  public get isTargetPopOutAvailable(): boolean {
    return !!this.targetPopup;
  }

  public setPopOut(popout: Window) {
    this.targetPopup = popout;
  }

  public getPopOut(): Window {
    return this.targetPopup;
  }

  /**
   * Sends a message to contained iFrame element
   * @param action class that implements Action
   * @param target the contained iFrame element
   */
  public sendMessageToTarget(action: Action) {
    const target = this.targetPopup || this.target?.contentWindow;
    if (!target) {
      Log.error("iFrame target not set");
      return;
    }
    target.postMessage(action, Config.iFrameOrigin);
  }

  /**
   *
   * @param target iFrame element to send messages to
   */
  public setTarget(target: HTMLIFrameElement) {
    this.target = target;
  }

  public get isTargetAvailable(): boolean {
    return !!this.target || !!this.targetPopup;
  }

  private onMessage = (event) => {
    if (!event.data) {
      return;
    }

    // discard events from not-trusted domains
    if (!Config.whitelistDomains.includes(event.origin)) {
      return;
    }

    this.windowMessageSubject.next(event.data);
  };

  public destroy() {
    window.removeEventListener("message", this.onMessage);
    this.windowMessageSubject?.complete();
    this.windowMessageSubject = null;
  }
}

export const WindowEventService = new WindowEventServiceCtr();
